import React from 'react';
import { useTranslation } from './i18n.js';
import { apiUrl } from './config.js';
import { decodeHtmlEntities } from './htmlEntities.js';
import { CollapsibleCard } from './collapsible.js';
import { openCard } from './collapsible.js';
import { productPath } from './collectionContext.js';
import { savePreference, readPreference, CATALOG_LANGUAGE_KEY } from './preferences.js';

// Lien dans le contexte de la collection affichée (/izvor/slug) : le fil d'Ariane de la page livre
// ramène ainsi à cette collection.
function ProductCard({ product, collection, labels, allLabel }) {
  const href = product.slug ? productPath(product.slug, collection, labels, allLabel) : null;
  const title = decodeHtmlEntities(product.title);
  const content = (
    <React.Fragment>
      {product.image && (
        <div className="collection-product__image-wrap">
          <img className="collection-product__image" src={product.image} alt={title || ''} loading="lazy" />
        </div>
      )}
      <div className="collection-product__body">
        {product.title && <h3 className="collection-product__title">{title}</h3>}
        {product.description && (
          <p className="collection-product__description">{decodeHtmlEntities(product.description)}</p>
        )}
        {product.isbn && <p className="collection-product__isbn">ISBN : {product.isbn}</p>}
      </div>
    </React.Fragment>
  );

  return href ? (
    <a className="collection-product" href={href}>
      {content}
    </a>
  ) : (
    <article className="collection-product">{content}</article>
  );
}

export function CollectionCard({ initialData }) {
  const { t } = useTranslation();
  const normalizeCode = (value) => String(value || '').trim();

  // L'API renvoie `selected.language` en minuscules (ex. "en") alors que les codes de `languages`
  // sont en majuscules (ex. "EN") : sans reconciliation, la valeur du <select> ne correspond à
  // aucune <option> et le navigateur affiche la première langue de la liste au lieu de la langue
  // réellement sélectionnée par l'API.
  const resolveCode = (list, rawValue) => {
    const normalized = normalizeCode(rawValue);
    const match = (list || []).find(
      (item) => String(item.code || '').toLowerCase() === normalized.toLowerCase()
    );
    return match ? match.code : normalized;
  };

  const [collections, setCollections] = React.useState(initialData ? initialData.collections : []);
  const [languages, setLanguages] = React.useState(initialData ? initialData.languages : []);
  const [products, setProducts] = React.useState(initialData ? initialData.products : []);
  const [selectedCollection, setSelectedCollection] = React.useState(
    resolveCode(initialData && initialData.collections, initialData && initialData.selected && initialData.selected.collection)
  );
  const [selectedLanguage, setSelectedLanguage] = React.useState(
    resolveCode(initialData && initialData.languages, initialData && initialData.selected && initialData.selected.language)
  );
  const [loading, setLoading] = React.useState(!initialData);
  const [error, setError] = React.useState('');
  // `searchText` suit la saisie ; `activeQuery` est la recherche réellement envoyée à l'API (validée
  // par Entrée ou le bouton), qui persiste quand on change de langue ou de collection ensuite.
  const [searchText, setSearchText] = React.useState('');
  const [activeQuery, setActiveQuery] = React.useState('');
  const [resultCount, setResultCount] = React.useState(null);

  const fetchCollection = (collectionCode, languageCode, query = activeQuery) => {
    setLoading(true);
    setError('');

    const url = apiUrl('collection');
    if (collectionCode) {
      url.searchParams.set('collection', collectionCode);
    }
    if (languageCode) {
      url.searchParams.set('language', languageCode);
    }
    if (query) {
      url.searchParams.set('q', query);
    }

    fetch(url.toString())
      .then((response) => {
        if (!response.ok) {
          throw new Error('load-error');
        }
        return response.json();
      })
      .then((data) => {
        const nextCollections = Array.isArray(data.collections) ? data.collections : [];
        const nextLanguages = Array.isArray(data.languages) ? data.languages : [];
        setCollections(nextCollections);
        setLanguages(nextLanguages);
        setProducts(Array.isArray(data.products) ? data.products : []);
        setSelectedCollection(resolveCode(nextCollections, data.selected && data.selected.collection));
        setSelectedLanguage(resolveCode(nextLanguages, data.selected && data.selected.language));
        setResultCount(data.selected && typeof data.selected.count === 'number' ? data.selected.count : null);
      })
      .catch(() => {
        setError(t('collectionErrorLoad'));
      })
      .finally(() => {
        setLoading(false);
      });
  };

  React.useEffect(() => {
    if (initialData) {
      return;
    }
    fetchCollection('', readPreference(CATALOG_LANGUAGE_KEY));
  }, []);

  // URL de collection (/izvor/) : le serveur a déjà chargé la sélection ; on amène le visiteur sur
  // la section, dépliée même s'il l'avait repliée.
  React.useEffect(() => {
    if (!initialData || !initialData.focus) {
      return;
    }
    openCard('collection');
    window.requestAnimationFrame(() => {
      const card = document.getElementById('collection');
      if (card) {
        card.scrollIntoView({ block: 'start' });
      }
    });
  }, []);

  const handleCollectionChange = (event) => {
    const nextCollection = event.target.value;
    setSelectedCollection(nextCollection);
    fetchCollection(nextCollection, selectedLanguage);
  };

  const handleLanguageChange = (event) => {
    const nextLanguage = event.target.value;
    setSelectedLanguage(nextLanguage);
    // Mémorisée (localStorage + cookie) : reprise par le serveur aux visites suivantes.
    savePreference(CATALOG_LANGUAGE_KEY, nextLanguage);
    fetchCollection(selectedCollection, nextLanguage);
  };

  // La saisie ne déclenche aucun appel : elle bascule seulement la collection sur « tout », pour
  // que la recherche porte sur l'ensemble du catalogue au moment de la validation.
  const handleSearchChange = (event) => {
    setSearchText(event.target.value);
    setSelectedCollection('tout');
  };

  const handleSearchSubmit = (event) => {
    event.preventDefault();
    const query = searchText.trim();
    setActiveQuery(query);
    fetchCollection(selectedCollection, selectedLanguage, query);
  };

  // L'API renvoie `direction` ('ltr'/'rtl') sur chaque entrée de `languages` : on réutilise
  // directement cette valeur (plutôt qu'une liste de codes RTL maintenue à la main) pour la langue
  // actuellement sélectionnée.
  const currentLanguage = languages.find(
    (language) => String(language.code || '').toLowerCase() === String(selectedLanguage).toLowerCase()
  );
  const textDirection = (currentLanguage && currentLanguage.direction) || 'ltr';

  return (
    <CollapsibleCard id="collection" className="collection-card" dir={textDirection} label={t('collectionCardTitle')}>
      <h2>{t('collectionCardTitle')}</h2>

      <form className="collection-search" aria-label={t('collectionSearchButton')} onSubmit={handleSearchSubmit}>
        <input
          type="search"
          className="collection-search__input"
          value={searchText}
          onChange={handleSearchChange}
          placeholder={t('collectionSearchPlaceholder')}
          aria-label={t('collectionSearchPlaceholder')}
        />
        <button type="submit" className="collection-search__submit" aria-label={t('collectionSearchButton')}>
          <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true" focusable="false">
            <circle cx="11" cy="11" r="7" fill="none" stroke="currentColor" strokeWidth="2" />
            <line x1="16.5" y1="16.5" x2="21" y2="21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          </svg>
        </button>
      </form>

      {activeQuery && resultCount !== null && (
        <p className="collection-search-summary">
          {t('collectionSearchResultsFor')} « {activeQuery} » : {resultCount}
        </p>
      )}

      <div className="collection-filters">
        {languages.length > 0 && (
          <div className="collection-filter">
            <label htmlFor="collection-language-select">{t('collectionLanguageLabel')}</label>
            <select id="collection-language-select" value={selectedLanguage} onChange={handleLanguageChange}>
              {languages.map((language) => (
                <option key={language.code} value={language.code}>
                  {language.name || language.code}
                </option>
              ))}
            </select>
          </div>
        )}

        {collections.length > 0 && (
          <div className="collection-filter">
            <label htmlFor="collection-select">{t('collectionSelectLabel')}</label>
            <select id="collection-select" value={selectedCollection} onChange={handleCollectionChange}>
              {collections.map((collection) => (
                <option key={collection.code} value={collection.code}>
                  {collection.label || collection.code}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>

      {loading ? (
        <div className="loading">
          <progress />
          <p>{t('collectionLoading')}</p>
        </div>
      ) : error ? (
        <div className="error">
          <strong>{t('collectionErrorPrefix')}</strong> {error}
        </div>
      ) : products.length === 0 ? (
        <p className="collection-empty">{t('collectionEmpty')}</p>
      ) : (
        <div className="collection-products">
          {products.map((product) => (
            <ProductCard
              product={product}
              collection={selectedCollection}
              labels={(initialData && initialData.labels) || {}}
              allLabel={t('collectionAll')}
              key={product.reference}
            />
          ))}
        </div>
      )}
      <p className="collection-in-progress">{t('collectionInProgress')}</p>
    </CollapsibleCard>
  );
}
